import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { Head } from '../../types';
import UtilizationGauge from './UtilizationGauge';

interface HeadBreakdownCardProps {
  head: Head;
  onClick?: () => void;
  showDetails?: boolean;
}

const HeadBreakdownCard: React.FC<HeadBreakdownCardProps> = ({
  head,
  onClick,
  showDetails = true,
}) => {
  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-200 p-4 ${
        onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
            style={{ backgroundColor: head.color }}
          >
            {head.code}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{head.name}</h3>
            <p className="text-xs text-gray-500">{head.subheadCount} subheads</p>
          </div>
        </div>
        {onClick && <ChevronRight className="w-5 h-5 text-gray-400" />}
      </div>

      {/* Gauge */}
      <div className="flex justify-center mb-4">
        <UtilizationGauge
          value={head.utilizationPercentage}
          size="md"
          label="Utilization"
        />
      </div>

      {/* Details */}
      {showDetails && (
        <div className="space-y-2">
          <div className="flex items-center justify-between py-2 border-t border-gray-100">
            <span className="text-sm text-gray-600">Sanctioned</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(head.totalSanctioned)}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-gray-100">
            <span className="text-sm text-gray-600">Spent (Rec)</span>
            <span className="text-sm font-semibold text-cyan-600">
              {formatCurrency((head as any).recurringSpent || 0)}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-gray-100">
            <span className="text-sm text-gray-600">Spent (Non-Rec)</span>
            <span className="text-sm font-semibold text-rose-600">
              {formatCurrency((head as any).nonRecurringSpent || 0)}
            </span>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="mt-4">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(head.utilizationPercentage, 100)}%`,
              backgroundColor: head.color,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default HeadBreakdownCard;
